'use client';

import { type FC, useMemo, useState } from 'react';

import { Container } from '../common/Container';
import { Text } from '../common/Text';

import { FILTER_EMPTY, FILTER_NOT_SELECTED_LABEL, getPaymentSystemTags } from './integrationScenarioHelpers';
import { INTEGRATION_SCENARIOS } from './integrationScenariosData';
import { IntegrationsFilterSelect } from './IntegrationsFilterSelect';
import styles from './IntegrationsScenariosBlock.module.scss';
import { FilterFunnelIcon, ResultsGridIcon } from './IntegrationsScenariosIcons';
import { ScenarioCard } from './ScenarioCard';

export const IntegrationsScenariosBlock: FC = () => {
  const regions = useMemo(
    () => Array.from(new Set(INTEGRATION_SCENARIOS.map((s) => s.geo))).sort((a, b) => a.localeCompare(b)),
    []
  );

  const currencies = useMemo(
    () => Array.from(new Set(INTEGRATION_SCENARIOS.flatMap((s) => s.currencies))).sort((a, b) => a.localeCompare(b)),
    []
  );

  const paymentTags = useMemo(
    () =>
      Array.from(new Set(INTEGRATION_SCENARIOS.flatMap((s) => getPaymentSystemTags(s)))).sort((a, b) =>
        a.localeCompare(b)
      ),
    []
  );

  const [transactionType, setTransactionType] = useState<string>(FILTER_EMPTY);
  const [region, setRegion] = useState<string>(FILTER_EMPTY);
  const [currency, setCurrency] = useState<string>(FILTER_EMPTY);
  const [method, setMethod] = useState<string>(FILTER_EMPTY);
  const [paymentSystem, setPaymentSystem] = useState<string>(FILTER_EMPTY);

  const filtered = useMemo(() => {
    return INTEGRATION_SCENARIOS.filter((s) => {
      if (transactionType && s.transactionType !== transactionType) {
        return false;
      }

      if (region && s.geo !== region) {
        return false;
      }

      if (currency && !s.currencies.includes(currency)) {
        return false;
      }

      if (method && s.method !== method) {
        return false;
      }

      if (paymentSystem && !getPaymentSystemTags(s).includes(paymentSystem)) {
        return false;
      }

      return true;
    });
  }, [transactionType, region, currency, method, paymentSystem]);

  return (
    <Container className={styles.root}>
      <div className={styles.filterPanel}>
        <div className={styles.filterTitleRow}>
          <span className={styles.filterTitleIcon}>
            <FilterFunnelIcon />
          </span>

          <Text type="medium18" color="white">
            Filter Integrations
          </Text>
        </div>

        <div className={styles.filterGrid}>
          <IntegrationsFilterSelect
            fieldId="integrations-filter-transaction-type"
            label="Transaction Type"
            value={transactionType}
            placeholderLabel={FILTER_NOT_SELECTED_LABEL}
            options={[
              { value: 'pay-in', label: 'Pay-in' },
              { value: 'pay-out', label: 'Pay-out' },
            ]}
            onChange={setTransactionType}
          />

          <IntegrationsFilterSelect
            fieldId="integrations-filter-region"
            label="Region (GEO)"
            value={region}
            placeholderLabel={FILTER_NOT_SELECTED_LABEL}
            options={regions.map((r) => ({ value: r, label: r }))}
            onChange={setRegion}
          />

          <IntegrationsFilterSelect
            fieldId="integrations-filter-currency"
            label="Currency"
            value={currency}
            placeholderLabel={FILTER_NOT_SELECTED_LABEL}
            options={currencies.map((c) => ({ value: c, label: c }))}
            onChange={setCurrency}
          />

          <IntegrationsFilterSelect
            fieldId="integrations-filter-method"
            label="Payment Method"
            value={method}
            placeholderLabel={FILTER_NOT_SELECTED_LABEL}
            options={[
              { value: 'card', label: 'Card' },
              { value: 'apm', label: 'APM' },
            ]}
            onChange={setMethod}
          />

          <IntegrationsFilterSelect
            fieldId="integrations-filter-payment-system"
            label="Payment System"
            value={paymentSystem}
            placeholderLabel={FILTER_NOT_SELECTED_LABEL}
            options={paymentTags.map((tag) => ({ value: tag, label: tag }))}
            onChange={setPaymentSystem}
          />
        </div>
      </div>

      <div className={styles.resultsHeader}>
        <div className={styles.resultsTitleRow}>
          <span className={styles.resultsGridIcon}>
            <ResultsGridIcon />
          </span>

          <h2 className={styles.resultsTitle}>Integration Scenarios</h2>

          <span className={styles.resultsBadge}>{filtered.length} results</span>
        </div>
      </div>

      <div className={styles.cardsGrid}>
        {filtered.length === 0 ? (
          <Text type="regular16" color="white50" whiteSpace="normal" className={styles.emptyState}>
            No integrations match these filters. Try adjusting your selections.
          </Text>
        ) : (
          filtered.map((scenario) => <ScenarioCard key={scenario.id} scenario={scenario} />)
        )}
      </div>
    </Container>
  );
};
